/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Stage,
  Image,
  Layer,
  Line,
  Rect,
  Circle,
} from 'react-konva';
import { Html } from 'react-konva-utils';
import clsx from 'clsx';
import useImage from 'use-image';
import useStyles from './styles';

import ImageLines from './ImageTypes/imageLines';
import ImageRects from './ImageTypes/imageRects';

const REFERENCE_WIDTH = 1440;
const REFERENCE_HEIGHT = 900;
const IMAGE_OFFSET = 2;

const calculateImageFactor = (image, viewportWidth, viewportHeight) => {
  const availableWidth = viewportWidth / (1.1 * 3.16);
  const availableHeight = viewportHeight / (1.1 * 1.2);
  return Math.min(availableWidth / image.width, availableHeight / image.height);
};

const SurveyImage = ({
  buttonColor, lines, erase, questionResponseImage,
  setLineList, shareType,
  polygonMode, polygonCache, setPolygonCache, polygonColor,
  colorOptions, selectedSubCategoryId = 'general',
}) => {
  // Rect se debe sacar con una request a la api.
  const [initialPoint, setInitialPoint] = useState({ x1: null, y1: null });
  const [currentPoint, setCurrentPoint] = useState({ x: null, y: null });
  const [drawing, setDrawing] = useState(false);
  const [factor, setFactor] = useState(null);
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [polygonId, setPolygonId] = useState(0);

  useEffect(() => {
    setPolygonPoints([]);
  }, [questionResponseImage.id]);

  const styles = useStyles();
  const isGeneralSubCategory = selectedSubCategoryId === 'general';
  const isVectorForSelectedSubCategory = (vector) => (
    isGeneralSubCategory
      ? vector.subCategoryId === null || vector.subCategoryId === undefined
      : Number(vector.subCategoryId) === Number(selectedSubCategoryId)
  );
  const getVisiblePolygons = (polygons = []) => polygons.filter(isVectorForSelectedSubCategory);
  const replaceSelectedSubCategoryVectors = (vectors, selectedVectors) => ([
    ...vectors.filter((vector) => !isVectorForSelectedSubCategory(vector)),
    ...selectedVectors,
  ]);
  // El id se calcula sobre todos los trazos de la foto, no sólo los visibles, para
  // que no se repita entre subcategorías (cleanLine borra por id).
  const getNextLineId = (vectors = []) => vectors.reduce(
    (maxId, vector) => Math.max(maxId, Number(vector.id) || 0),
    0,
  ) + 1;
  const [imageTest] = useImage(questionResponseImage.imageUrl);
  const referenceFactor = imageTest
    ? calculateImageFactor(imageTest, REFERENCE_WIDTH, REFERENCE_HEIGHT)
    : 1;
  const overlayScale = (factor || 1) / referenceFactor;

  const getReferencePoint = (x, y) => ({
    x: Math.round(IMAGE_OFFSET + ((x - IMAGE_OFFSET) * referenceFactor) / (factor || 1)),
    y: Math.round(IMAGE_OFFSET + ((y - IMAGE_OFFSET) * referenceFactor) / (factor || 1)),
  });

  const getDisplayPoint = (point) => ({
    x: IMAGE_OFFSET + (point.x - IMAGE_OFFSET) * overlayScale,
    y: IMAGE_OFFSET + (point.y - IMAGE_OFFSET) * overlayScale,
  });

  const handleMouseDown = (event) => {
    if (polygonMode && polygonColor) {
      // Modo polígono
      const point = getReferencePoint(event.evt.layerX, event.evt.layerY);
      const newPoint = { x: point.x, y: point.y };
      setPolygonPoints([...polygonPoints, newPoint]);
    } else if (buttonColor) {
      // Modo línea/rectángulo normal
      setInitialPoint({ ...initialPoint, x1: event.evt.layerX, y1: event.evt.layerY });
      setDrawing(true);
    }
  };

  const handleMouseMove = (event) => {
    if (drawing) {
      setCurrentPoint({ ...currentPoint, x: event.evt.layerX, y: event.evt.layerY });
    }
  };

  const handleMouseUp = () => {
    setDrawing(false);
    setCurrentPoint({ ...currentPoint, x: null, y: null });
    if (buttonColor
      && initialPoint.x1 !== null
      && initialPoint.y1 !== null
      && currentPoint.x !== null
      && currentPoint.y !== null) {
      const initialImagePoint = getReferencePoint(initialPoint.x1, initialPoint.y1);
      const currentImagePoint = getReferencePoint(currentPoint.x, currentPoint.y);
      setLineList((previousLineList) => {
        const currentLines = previousLineList[questionResponseImage.id] || [];
        const line = {
          x1: initialImagePoint.x,
          y1: initialImagePoint.y,
          x2: currentImagePoint.x,
          y2: currentImagePoint.y,
          color: buttonColor,
          brand: colorOptions ? colorOptions[buttonColor] : null,
          subCategoryId: isGeneralSubCategory ? null : Number(selectedSubCategoryId),
          id: getNextLineId(currentLines),
          guardada: false,
        };

        return {
          ...previousLineList,
          [questionResponseImage.id]: [...currentLines, line],
        };
      });
    }
  };

  const handleEventListener = () => {
    setDrawing(false);
    setInitialPoint({
      ...initialPoint,
      x1: null,
      y1: null,
    });
    setCurrentPoint({
      ...currentPoint,
      x: null,
      y: null,
    });
  };

  useEffect(() => {
    if (drawing) {
      // eslint-disable-next-line no-undef
      document.body.style.cursor = 'pointer';
    } else {
      // eslint-disable-next-line no-undef
      document.body.style.cursor = 'context-menu';
    }
  }, [drawing]);

  useEffect(() => {
    window.addEventListener('mouseup', handleEventListener);
    return () => {
      window.removeEventListener('mouseup', handleEventListener);
    };
  }, []);

  const cleanLine = (id) => {
    if (erase) {
      setLineList((previousLineList) => {
        const currentLines = previousLineList[questionResponseImage.id] || [];

        return {
          ...previousLineList,
          [questionResponseImage.id]: replaceSelectedSubCategoryVectors(
            currentLines,
            currentLines
              .filter(isVectorForSelectedSubCategory)
              .filter((item) => item.id !== id),
          ),
        };
      });
    }
  };

  // Se usa la razon: 432 x 540 (ancho por alto)
  // Espacio maximo:
  // inner.width / 3.16
  // inner.height / 1.2

  const getUseDimensions = (image) => {
    setFactor(calculateImageFactor(image, window.innerWidth, window.innerHeight));
  };

  // Esto se esta calculando en cada vez
  // No encuentro otra forma
  useEffect(() => {
    // quizas se puede almacenar el ancho primero y el de despues, para ver si tirar o no la wea
    if (imageTest) {
      getUseDimensions(imageTest);
    }
  }, [imageTest]);

  useEffect(() => {
    const handleResize = () => {
      if (imageTest) {
        getUseDimensions(imageTest);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageTest]);

  // Función para completar polígono (click sobre un vértice del trazo actual o tecla Enter)
  const completePolygon = () => {
    if (polygonPoints.length >= 3) {
      const newPolygon = {
        id: `polygon_${polygonId}_${Date.now()}`,
        points: [...polygonPoints],
        color: polygonColor || 'green',
        brand: colorOptions ? colorOptions[polygonColor] || 0 : 0,
        subCategoryId: isGeneralSubCategory ? null : Number(selectedSubCategoryId),
        timestamp: new Date().toISOString(),
        guardada: false,
      };

      setPolygonCache({
        ...polygonCache,
        [questionResponseImage.id]: [
          ...(polygonCache[questionResponseImage.id] || []),
          newPolygon,
        ],
      });
      setPolygonPoints([]);
      setPolygonId(polygonId + 1);
    }
  };

  // Escuchar tecla Enter para completar polígono
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter' && polygonMode && polygonPoints.length >= 3) {
        completePolygon();
      }
      if (event.key === 'Escape' && polygonMode) {
        setPolygonPoints([]);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [polygonMode, polygonPoints]);

  // NOTE: every colour inside the <Stage> below is intentionally NOT themed.
  // These are Konva shapes painted on top of the audited photo (stroke colours
  // chosen by the auditor, white point outlines, and the black-on-white
  // measurement label). They have to stay readable against the photograph, not
  // against the page surface, so theme tokens do not apply here.
  return (
    <>
      <Stage
        className={clsx({
          [styles.imageCanva]: true,
        })}
        width={(imageTest?.width || 0) * factor + 4}
        height={(imageTest?.height || 0) * factor + 4}
      >
        <Layer>
          {/* <Rect
            x={0}
            y={0}
            width={window.innerWidth / (3.16 * 1.1) + 2}
            height={window.innerHeight / (1.1 * 1.2) }
            stroke='black'
          /> */}
          <Image
            image={imageTest}
            scale={{ x: factor, y: factor }}
            x={2}
            y={2}
            onMouseDown={(e) => {
              handleMouseDown(e);
            }}
            onMouseUp={(e) => {
              handleMouseUp(e);
            }}
            onMouseMove={(e) => {
              handleMouseMove(e);
            }}
          />
        {/* Polígonos en progreso */}
        {polygonPoints.length > 0 && (
          <>
            {/* Líneas del polígono */}
            {polygonPoints.length > 1 && (
              <Line
                points={polygonPoints.flatMap((point) => {
                  const displayPoint = getDisplayPoint(point);
                  return [displayPoint.x, displayPoint.y];
                })}
                stroke={polygonColor || 'blue'}
                strokeWidth={2}
                closed={false}
              />
            )}
            {/* Puntos del polígono: click sobre cualquiera de ellos cierra el polígono */}
            {polygonPoints.map((point, index) => (
              <Circle
                key={index}
                x={getDisplayPoint(point).x}
                y={getDisplayPoint(point).y}
                radius={4}
                fill={polygonColor || 'blue'}
                stroke="white"
                strokeWidth={1}
                hitStrokeWidth={8}
                onMouseDown={() => {
                  if (polygonMode && polygonPoints.length >= 3) {
                    completePolygon();
                  }
                }}
              />
            ))}
          </>
        )}
        
        {/* Polígonos completados */}
        {getVisiblePolygons(polygonCache[questionResponseImage.id] || []).map((polygon) => (
          <>
            <Line
              key={`polygon-${polygon.id}`}
              points={polygon.points.flatMap((point) => {
                const displayPoint = getDisplayPoint(point);
                return [displayPoint.x, displayPoint.y];
              })}
              stroke={polygon.color || 'green'}
              strokeWidth={2}
              fill={polygon.color || 'green'}
              opacity={0.3}
              closed={true}
              listening={erase}
              onClick={() => {
                if (erase) {
                  // Eliminar polígono
                  const currentPolygons = polygonCache[questionResponseImage.id] || [];
                  const updatedPolygons = replaceSelectedSubCategoryVectors(
                    currentPolygons,
                    getVisiblePolygons(currentPolygons)
                      .filter((polygonToKeep) => polygonToKeep.id !== polygon.id),
                  );
                  setPolygonCache({
                    ...polygonCache,
                    [questionResponseImage.id]: updatedPolygons,
                  });
                }
              }}
            />
            {polygon.points.map((point, index) => (
              <Circle
                key={`${polygon.id}-point-${index}`}
                x={getDisplayPoint(point).x}
                y={getDisplayPoint(point).y}
                radius={3}
                fill={polygon.color || 'green'}
                stroke="white"
                strokeWidth={1}
                listening={false}
              />
            ))}
          </>
        ))}

        { drawing && currentPoint.x && initialPoint.x1
          ? <>
            { shareType === 'linear-share'
              ? (<>
                  <Html>
                    <p align="right" style={{ color: 'black', paddingLeft: '10px', backgroundColor: 'white' }}>
                      <b>
                        {Math.hypot(currentPoint.x - initialPoint.x1,
                          currentPoint.y - initialPoint.y1).toFixed(0)}
                      </b>
                    </p>
                  </Html>
                  <Line
                    onMouseUp={(e) => {
                      handleMouseUp(e);
                    }}
                    onMouseMove={(e) => {
                      handleMouseMove(e);
                    }}
                    key="drawing-line"
                    x={0}
                    y={0}
                    points={ [initialPoint.x1, initialPoint.y1, currentPoint.x, currentPoint.y] }
                    stroke={buttonColor}
                    strokeWidth={5}
                    />
                 </>
              )
              : (<Rect
                  onMouseUp={(e) => {
                    handleMouseUp(e);
                  }}
                  onMouseMove={(e) => {
                    handleMouseMove(e);
                  }}
                  x={initialPoint.x1}
                  y={initialPoint.y1}
                  width={currentPoint.x - initialPoint.x1}
                  height={currentPoint.y - initialPoint.y1}
                  stroke={buttonColor}
                  fill={buttonColor}
                  opacity={0.4}
                />
              )
              }
            </>
          : null
        }
        </Layer>
        {shareType === 'linear-share'
          ? <ImageLines
              lines={lines}
              scale={overlayScale}
              cleanLine={cleanLine}
              handleMouseUp={handleMouseUp}
              handleMouseDown={handleMouseDown}
              handleMouseMove= {handleMouseMove}
              />
          : <ImageRects
              lines={lines}
              scale={overlayScale}
              cleanLine={cleanLine}
              handleMouseUp={handleMouseUp}
              handleMouseDown={handleMouseDown}
              handleMouseMove={handleMouseMove}
            />
        }
      </Stage>

    </>
  );
};

export default SurveyImage;
/* eslint-enable */
